import type { SourcedCandidate } from "./hosted-evidence-projection";
import { bytesSha256 } from "./persistence/supabase/checksum";

export type ChannelVerificationStatus = "Needs checking" | "Confirmed" | "Rejected";

export interface HostedChannelVerification {
  organisationId: string;
  candidateKey: string;
  label: string;
  source: string;
  sourceUrl: string | null;
  canonicalUrl: string | null;
  status: ChannelVerificationStatus;
  revision: number;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export interface SaveChannelVerification {
  organisationId: string;
  candidate: SourcedCandidate;
  canonicalUrl: string | null;
  status: ChannelVerificationStatus;
  expectedRevision: number;
}

export interface SaveChannelVerificationV1Command {
  schemaVersion: 1;
  organisationId: string;
  candidateKey: string;
  label: string;
  source: string;
  sourceUrl: string | null;
  canonicalUrl: string | null;
  status: ChannelVerificationStatus;
  expectedRevision: number;
  idempotencyKey: string;
  eventId: string;
}

export function validChannelUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && Boolean(url.hostname) && !url.username && !url.password;
  } catch { return false; }
}

/** URL parsing normalises host casing and default ports while retaining path, query and fragment. */
export function normaliseChannelUrl(value: string): string | undefined {
  if (!validChannelUrl(value)) return;
  return new URL(value).href;
}

export async function operatorChannelCandidateKey(canonicalUrl: string): Promise<string> {
  const normalised = normaliseChannelUrl(canonicalUrl);
  if (!normalised) throw new Error("Enter a valid HTTP or HTTPS URL without credentials.");
  const digest = await bytesSha256(new TextEncoder().encode(normalised));
  return `manual-url:${digest}`;
}

export function findEquivalentChannelRecord(
  canonicalUrl: string,
  candidateKey: string,
  candidates: SourcedCandidate[],
  saved: HostedChannelVerification[],
): { candidate: SourcedCandidate; saved?: HostedChannelVerification } | undefined {
  const target = normaliseChannelUrl(canonicalUrl);
  if (!target) return;
  const savedMatch = saved.find(item => item.candidateKey === candidateKey || [item.canonicalUrl, item.sourceUrl].some(url => url && normaliseChannelUrl(url) === target));
  if (savedMatch) {
    const candidate = candidates.find(item => item.key === savedMatch.candidateKey) ?? {
      key: savedMatch.candidateKey,
      label: savedMatch.label,
      source: savedMatch.source,
      url: savedMatch.sourceUrl ?? undefined,
      confidence: "Candidate — ownership unconfirmed" as const,
    };
    return { candidate, saved: savedMatch };
  }
  const candidate = candidates.find(item => item.key === candidateKey || Boolean(item.url && normaliseChannelUrl(item.url) === target));
  return candidate ? { candidate, saved: saved.find(item => item.candidateKey === candidate.key) } : undefined;
}

export function channelVerificationView(candidate: SourcedCandidate, saved?: HostedChannelVerification) {
  const sourceUrl = candidate.url?.trim() || null;
  const canonicalUrl = saved ? saved.canonicalUrl : (sourceUrl && validChannelUrl(sourceUrl) ? sourceUrl : null);
  return {
    ...candidate,
    sourceUrl,
    canonicalUrl,
    status: saved?.status ?? "Needs checking" as ChannelVerificationStatus,
    revision: saved?.revision ?? 0,
    reviewedAt: saved?.reviewedAt ?? null,
    reviewedBy: saved?.reviewedBy ?? null,
  };
}
